/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import "./index.css";
import lottie from "lottie-web";
import data from "@/assets/xloading.json";
import MobileMorePanel from "@/component/AudioPlayer/components/MobileMorePanel";
import { VideoPlayerIns } from "./VideoPlayerIns";
import avPlayerStore from "@/store/avPlayer.store";
import summaryCommonStore from "@/store/summaryCommon.store";
import IcVideoLoading from "@/assets/app/app_loading.svg?react";
import IcVideoError from "@/assets/app/app_error.svg?react";

export interface VideoPlayerRef {
  play: () => void;
  pause: () => void;
  setCurrentTime: (currentTime: number) => void;
}

interface VideoPlayerProps {
  videoUrl: string | undefined;
  videoLoadFailure: boolean;
  onVideoInsLoaded?: (videoIns: VideoPlayerIns) => void;
}

const VideoPlayer = forwardRef<VideoPlayerRef, VideoPlayerProps>(
  (props, ref) => {
    const { videoUrl, videoLoadFailure, onVideoInsLoaded } = props;
    const videoRef = useRef<HTMLVideoElement>(null);
    const [showHoverTool, setShowHoverTool] = useState(false);
    const [videoLoaded, setVideoLoaded] = useState(false);
    const [videoWaiting, setVideoWaiting] = useState(false);
    const [videoError, setVideoError] = useState(false);

    const [rateText, setRateText] = useState("1.0"); // 播放速度显示文本

    const [showMorePanel, setShowMorePanel] = useState(false);

    const totalDuration = useRef(0);
    const lottieRef = useRef(null);

    useEffect(() => {
      if (!videoUrl) return;
      if (!videoLoaded || videoWaiting) {
        lottie.destroy();
        lottie.loadAnimation({
          container: lottieRef.current!,
          loop: true,
          autoplay: true,
          renderer: "svg",
          animationData: data,
        });
      } else {
        lottie.destroy();
      }

      return () => {
        lottie.destroy();
      };
    }, [videoLoaded, videoWaiting]);

    useEffect(() => {
      if (!videoUrl) return;
      const video = videoRef.current;
      if (!video) return;
      const videoPlayerIns = new VideoPlayerIns(video, {
        onLoadedData: (duration: number) => {
          totalDuration.current = duration;
          setVideoLoaded(true);
        },
        onError: () => {
          setVideoError(true);
        },
        onWaiting: () => {
          setVideoWaiting(true);
        },
        onSeeked: () => {
          summaryCommonStore.getState().setIsScrolled(false);
        },
        onCanPlay: () => {
          setVideoWaiting(false);
        },
        onTimeUpdate: (time) => {
          avPlayerStore.getState().updatePlayingTime(time);
        },
      });

      videoPlayerIns.loadVideo(videoUrl);
      onVideoInsLoaded && onVideoInsLoaded(videoPlayerIns);
      return () => {
        videoPlayerIns.destroy();
      };
    }, [videoUrl]);

    const play = () => {
      console.log("click play");
      videoRef.current && videoRef.current.play();
    };

    const pause = () => {
      console.log("click pause");
      videoRef.current && videoRef.current.pause();
    };

    const setCurrentTime = (currentTime: number) => {
      console.log("click setCurrentTime");
      if (videoRef.current) {
        videoRef.current.currentTime = currentTime;
      }
    };

    useImperativeHandle(ref, () => ({
      play,
      pause,
      setCurrentTime,
    }));

    const handleChangePlayRate = (rate: string) => {
      setRateText(rate);
      videoRef.current && (videoRef.current.playbackRate = Number(rate));
    };

    return (
      <>
        <div className="w-full h-55 relative">
          <MobileMorePanel
            visible={showMorePanel}
            playRate={rateText}
            setPlayRate={handleChangePlayRate}
            setVisible={setShowMorePanel}
            showSkipBlank={true}
            skipBlank={false}
            setSkipBlank={() => { }}
          />
          <div
            className="absolute top-0 left-0 w-full h-full bg-#000"
            onClick={() => {
              setShowHoverTool(!showHoverTool);
            }}
          >
            {
              videoUrl ? (<video
                ref={videoRef}
                width="100%"
                height="100%"
                playsInline
                controls
              />
              ) : (
                videoLoadFailure ? (
                  <div className=" flex flex-col items-center justify-center w-full h-full" onClick={() => {
                    summaryCommonStore.getState().refreshMediaInfo();
                  }}>
                    <IcVideoError className="size-10" />
                    <span className="text-#FFFFFF text-size-3.5">网络异常，点击刷新</span>
                  </div>
                ) : (
                  <div className=" flex flex-col items-center justify-center w-full h-full">
                    <IcVideoLoading className="rotation size-10" />
                    <span className="text-#FFFFFF text-size-3.5">上传中</span>
                  </div>
                )
              )
            }
          </div>

          {videoError && (
            <div className="absolute flex flex-col items-center justify-center z-90 bg-black w-full h-full select-none rd-2">
              <span className="text-#FFFFFFCC text-size-4 mb-2">网络异常</span>
              <span className="text-#FFFFFF66 text-size-4">
                请试试
                <span
                  className="ml-1 text-#4981F2 cursor-pointer"
                  onClick={() => {
                    setVideoError(false);
                    play();
                  }}
                >
                  刷新
                </span>
              </span>
            </div>
          )}
        </div>
      </>
    );
  }
);

export default VideoPlayer;
